import { atom } from 'jotai';
import { atomWithLazy, loadable, unwrap } from 'jotai/utils';
import { IField, isUniversalKPIField } from '../../data-models/field2.data-model';
import { KPIGroup } from '../../schemas/kpi-group.schema';
import { sortFields } from '../../util/field-utils';
import { getForesightStore } from '../../util/jotai-store';
import { getAllKPIs, getKPIGroupsSettings } from '../queries/KPIConfigQueries';
import { RendererType } from '../../data-models/field-definition';
import { captureUnexpectedState } from '../../util/SentryUtils';
import { FieldDefinition } from '../../data-models/field-definition/field-definition.schema';
import { kpiConfigToFieldDefinition } from '../../data-models/field-definition/kpi-config-mapping';

export const kpiConfigAtom = atomWithLazy(async () => {
  const res = await getAllKPIs();
  return res.sort(sortFields);
});

export const kpiConfigLoadable = loadable(kpiConfigAtom);

export const kpiConfigByIdMapAtom = atom(async (get) => {
  const map = new Map<number, IField<unknown>>();
  const kpiConfig = await get(kpiConfigAtom);

  if (!kpiConfig) {
    return map;
  }

  return kpiConfig.reduce((res, kpi) => {
    res.set(kpi.id, kpi);
    return res;
  }, map);
});

export const kpiConfigByKeyMapAtom = atom(async (get) => {
  const map = new Map<string, IField<unknown>>();
  const kpiConfig = await get(kpiConfigAtom);
  if (!kpiConfig) {
    return map;
  }
  return kpiConfig.reduce((res, kpi) => {
    const key = getKpiFieldKey(kpi);
    if (!key) {
      captureUnexpectedState('KPI entityField is missing', { kpi });
      return res;
    }
    res.set(key, kpi);
    return res;
  }, map);
});

export const kpiConfigByKeyMapAtomLoadable = loadable(kpiConfigByKeyMapAtom);

export const kpiGroupsAtom = atomWithLazy(async () => {
  const res = await getKPIGroupsSettings();

  return res.sort((s1, s2) => s1.sortOrder - s2.sortOrder);
});

export const kpiGroupsByIdAtom = atom(async (get) => {
  const groups = await get(kpiGroupsAtom);

  return groups.reduce((res, group) => {
    res.set(group.id, group);
    return res;
  }, new Map<number, KPIGroup>());
});

export const universalAndSystemKPIAtom = atom((get) => {
  const kpis = get(unwrap(kpiConfigAtom));

  return kpis?.filter(isUniversalKPIField) ?? [];
});

export function getKpiFieldKey(kpi?: IField<unknown>) {
  return kpi?.entityField;
}

export function getKpisByKey() {
  const store = getForesightStore();
  const kpisByKeyLoadable = store.get(kpiConfigByKeyMapAtomLoadable);
  return kpisByKeyLoadable.state === 'hasData' ? kpisByKeyLoadable.data : new Map<string, IField<unknown>>();
}

export const kpiConfigByNameAtom = atom(async (get) => {
  const kpiConfig = await get(kpiConfigAtom);
  return new Map(kpiConfig?.map((kpi) => [kpi.displayName, kpi]));
});

export const quantitativeKpiConfigAtom = atom(async (get) => {
  const kpiConfig = await get(kpiConfigAtom);
  return kpiConfig?.filter((kpi) => !isTextualKpiField(kpi));
});

/**
 * Sync view of the quantitative KPIs, holding the last value while a refetch is
 * in flight. Read by the formula editor, whose vocabulary is rebuilt on every
 * render — an inline unwrap() there would be a fresh atom each time.
 */
export const quantitativeKpiConfigUnwrapped = unwrap(
  quantitativeKpiConfigAtom,
  (prev) => prev ?? ([] as IField<unknown>[])
);

export const textualKpiConfigAtom = atom(async (get) => {
  const kpiConfig = await get(kpiConfigAtom);
  return kpiConfig?.filter(isTextualKpiField);
});

export const textualKpisByIdAtom = atom(async (get) => {
  const textualKpis = await get(textualKpiConfigAtom);
  return textualKpis.reduce((res, kpi) => {
    return res.set(kpi.id, kpi);
  }, new Map<number, IField<unknown>>());
});

export function isTextualKpiField({ type, formMeta }: IField<unknown>) {
  return type === 'string' && formMeta?.renderer?.type === RendererType.text;
}

/**
 * Canonical KPI field definitions, derived from `kpiConfigAtom` (single
 * network call). Per the FD-2794 cross-cutting rule, the legacy atom is
 * the source of truth during migration; this atom only re-projects.
 *
 * Per-record `try`/`catch` is defense-in-depth for shape changes the
 * schema's transforms can't absorb (e.g. a BE type migration like
 * `kpiGroupId: number` → `string`). A single bad record drops out and
 * gets logged to Sentry rather than taking down the whole financials
 * grid via the React error boundary.
 */
export const kpiDefinitionAtom = atom(async (get): Promise<FieldDefinition[]> => {
  const kpis = await get(kpiConfigAtom);
  return kpis.flatMap((kc) => {
    try {
      return [kpiConfigToFieldDefinition(kc)];
    } catch (e) {
      captureUnexpectedState(`kpiConfigToFieldDefinition cast failed for KPI id=${kc.id}`, {
        error: String(e),
        kc,
      });
      return [];
    }
  });
});

export const kpiDefinitionByKeyMapAtom = atom(async (get) => {
  const defs = await get(kpiDefinitionAtom);
  return new Map(defs.map((d) => [d.key, d]));
});

export const kpiDefinitionByNameAtom = atom(async (get) => {
  const defs = await get(kpiDefinitionAtom);
  return new Map(defs.map((d) => [d.displayName, d]));
});

export const kpiDefinitionLoadable = loadable(kpiDefinitionAtom);
export const kpiDefinitionByKeyMapAtomLoadable = loadable(kpiDefinitionByKeyMapAtom);
